import type { Bench } from './ctx';
import { COLS, pct100 } from './lib';
import { saveBlob, textPdf } from '../../ui/textPdf';

// The QBR as a print-ready page (the browser saves it as a PDF), restored from production
// (scorecard_qbr_pdf.dart): the building's composite and grade, every measure with its
// percentile against the named peer set. Built from the same answer the page shows, so a
// measure the server could not rank prints as not ranked, never a guess.

/** 82 -> "82nd", 11 -> "11th". */
export const ordinal = (n: number) => {
  const t = n % 100;
  return n + (t >= 11 && t <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th');
};
const esc = (s: unknown) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

export function qbrHtml(fac: Bench, peerLabel: string, now: Date = new Date()): string {
  const rows = COLS.map((c) => {
    const m = fac.metrics?.[c.key];
    const p = pct100(m?.percentile);
    return `<tr><td>${esc(c.label)}</td><td>${m?.value == null ? 'no value' : esc(m.value)}</td><td>${p == null ? 'not ranked' : ordinal(p)}</td></tr>`;
  }).join('');
  const quarter = `Q${Math.floor(now.getUTCMonth() / 3) + 1} ${now.getUTCFullYear()}`;
  return `<!doctype html><html><head><meta charset="utf-8"><title>Quarterly business review, ${esc(fac.name)}</title>
<style>body{font-family:Archivo,Arial,sans-serif;color:#22272E;margin:32px}h1{font-size:20px;margin:0 0 4px}table{border-collapse:collapse;width:100%;margin-top:14px}td,th{border-bottom:1px solid #ddd;padding:6px;text-align:left;font-size:12px}.m{color:#54606C;font-size:11px}</style></head>
<body><h1>Quarterly business review: ${esc(fac.name)}</h1>
<p class="m">${quarter}${fac.ccn ? ' · CCN ' + esc(fac.ccn) : ''}${fac.beds != null ? ' · ' + fac.beds + ' beds' : ''} · peers: ${esc(peerLabel)}${fac.peerN && !/n=\d/.test(peerLabel) ? ' (' + fac.peerN + ')' : ''}</p>
<p><b>Composite ${fac.composite == null ? 'not scored' : esc(fac.composite)}${fac.grade ? ' · grade ' + esc(fac.grade) : ''}</b></p>
<table><tr><th>Measure</th><th>Value</th><th>Percentile vs peers</th></tr>${rows}</table>
<p class="m">From CMS data as Aithora reads it${fac.insufficient ? '; too few peers to grade' : ''}. Percentiles are unadjusted: a higher-acuity building will read worse than it performs.</p></body></html>`;
}

/** Opens the QBR in a print window. False when the browser blocked the window, so the page can say so. */
export function printQbr(fac: Bench, peerLabel: string): boolean {
  const w = window.open('', '_blank');
  if (!w) return false;
  w.document.write(qbrHtml(fac, peerLabel));
  w.document.close();
  w.focus();
  w.print();
  return true;
}

/** The QBR as a PDF file, the same lines as the print page, saved straight away (no print window). */
export function qbrPdfLines(fac: Bench, peerLabel: string, now: Date = new Date()): { title: string; sub: string; lines: string[] } {
  const quarter = `Q${Math.floor(now.getUTCMonth() / 3) + 1} ${now.getUTCFullYear()}`;
  const lines = [
    `Composite ${fac.composite == null ? 'not scored' : fac.composite}${fac.grade ? ' · grade ' + fac.grade : ''}`,
    '',
    ...COLS.map((c) => {
      const m = fac.metrics?.[c.key];
      const p = pct100(m?.percentile);
      return `${c.label}: ${m?.value == null ? 'no value' : m.value} · ${p == null ? 'not ranked' : ordinal(p) + ' percentile vs peers'}`;
    }),
    '',
    `From CMS data as Aithora reads it${fac.insufficient ? '; too few peers to grade' : ''}. Percentiles are unadjusted: a higher-acuity building will read worse than it performs.`,
  ];
  return {
    title: `Quarterly business review: ${fac.name}`,
    sub: `${quarter}${fac.ccn ? ' · CCN ' + fac.ccn : ''}${fac.beds != null ? ' · ' + fac.beds + ' beds' : ''} · peers: ${peerLabel}${fac.peerN && !/n=\d/.test(peerLabel) ? ' (' + fac.peerN + ')' : ''}`,
    lines,
  };
}

export function downloadQbrPdf(fac: Bench, peerLabel: string): void {
  const d = qbrPdfLines(fac, peerLabel);
  const stem = String(fac.name ?? 'building').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  saveBlob(textPdf(d.title, d.sub, d.lines), `aithora-qbr-${stem}.pdf`);
}
